import { Platform } from 'react-native';

import type { StagedAttachment } from '@/components/work-orders/attachments-staging';
import { AttachmentType, type Attachment } from '@/types/models';
import { inferImageMimeType, isImageFile } from '@/utils/file-helpers';
import { nativeFormFile, type NativeFormFile } from '@/utils/native-form-file';

export interface StagedFilesFromDrop {
	/** Files accepted for staging, in the order they were supplied. */
	staged: StagedAttachment[];
	/** Names of files rejected because they aren't images. */
	rejected: string[];
}

/**
 * Converts DOM `File`s — from a drop event or a web `<input type="file">` —
 * into `StagedAttachment`s, rejecting anything that isn't an image. (#382)
 *
 * Images-only is a deliberate gate, not laziness: `api/files.ts` only serves
 * `image/*` and `video/*` back, and the work-order attachment UI refuses PDFs
 * outright. Without this check a dropped PDF stages happily and then fails at
 * upload time — i.e. AFTER the work order has already been created, which is
 * the most confusing moment possible to surface the error.
 *
 * The `file` field is populated verbatim, which is the fast path through
 * `resolveStagedFileForUpload` (it short-circuits before the `fetch(uri)`
 * fallback). The object URL is only used as a stable list key, so it's minted
 * lazily — never for a rejected file.
 */
export function stagedAttachmentsFromFiles(
	files: File[],
	deps: { createObjectUrl?: (file: File) => string } = {},
): StagedFilesFromDrop {
	const createObjectUrl = deps.createObjectUrl ?? ((file: File) => URL.createObjectURL(file));
	const staged: StagedAttachment[] = [];
	const rejected: string[] = [];

	for (const file of files) {
		if (!isImageFile(file.name, file.type)) {
			rejected.push(file.name);
			continue;
		}
		staged.push({
			uri: createObjectUrl(file),
			name: file.name,
			mimeType: file.type || inferImageMimeType(file.name),
			file,
		});
	}

	return { staged, rejected };
}

/**
 * Normalizes a `StagedAttachment` into a value safe to append as the
 * `file` field on a FormData payload, handling both native and web.
 *
 * Why this exists: a plain `{ uri, name, type }` object fails on BOTH
 * platforms. On native, expo/fetch refuses to encode it. On web it
 * gets coerced to the string `"[object Object]"` when the
 * browser serializes the multipart body — and then the server's
 * `formData.get('file')` returns a string, `file.arrayBuffer()` throws,
 * and the upload silently fails.
 *
 * Resolution order:
 *   1. If the picker gave us an actual File (web DOM case where
 *      `asset.file` is populated), use it verbatim.
 *   2. On web, fetch the URI (supports `blob:`, `data:`, `http(s):`) and
 *      wrap the resulting Blob in a File so the server sees `.name`.
 *   3. On native, a `NativeFormFile` — SDK 58's expo/fetch cannot encode
 *      RN's `{ uri, name, type }` descriptor (see utils/native-form-file.ts).
 */
export async function resolveStagedFileForUpload(
	staged: StagedAttachment,
	// Dependency-injected for tests; defaults to real platform / fetch.
	deps: { isWeb?: boolean; fetchImpl?: typeof fetch } = {},
): Promise<Blob | File | NativeFormFile> {
	const isWeb = deps.isWeb ?? Platform.OS === 'web';
	const fetchImpl = deps.fetchImpl ?? fetch;

	if (staged.file) {
		return staged.file;
	}

	if (isWeb) {
		const response = await fetchImpl(staged.uri);
		const blob = await response.blob();
		// Wrap in File so the server can read `file.name`. File is a Blob
		// subclass on web — server-side `formData.get('file')` will return
		// a File instance.
		if (typeof File !== 'undefined') {
			return new File([blob], staged.name, { type: staged.mimeType || blob.type });
		}
		return blob;
	}

	return nativeFormFile({ uri: staged.uri, name: staged.name, type: staged.mimeType });
}

export interface StagedUploadDeps {
	uploadFileToServer: (formData: FormData) => Promise<{
		success?: boolean;
		fileName?: string;
		fileType?: string;
		mimeType?: string;
		fileSize?: number;
		storageKey?: string;
	} | null | undefined>;
	createAttachment: (data: Partial<Attachment>) => Promise<unknown>;
	isDemoMode?: boolean;
	/** Override for tests; defaults to real Platform-aware behavior. */
	resolveFile?: typeof resolveStagedFileForUpload;
}

export interface StagedUploadResult {
	/** The subset of input files that uploaded successfully. */
	uploaded: StagedAttachment[];
	/** The subset that failed (for UX: keep these in the staging list). */
	failed: StagedAttachment[];
}

/**
 * Uploads a list of staged attachments sequentially and reports which ones
 * succeeded vs failed. The caller can then only clear the successes from
 * UI state, so a user with 3 photos where 1 fails keeps that 1 staged for
 * retry rather than losing all three.
 *
 * Pulled out of components/work-orders/create.tsx so the sequencing and
 * error-partitioning behavior is unit-testable without rendering the
 * parent component. (PR #127 round-2 review finding #3.)
 */
export async function uploadStagedAttachments(
	files: StagedAttachment[],
	workOrderId: string,
	deps: StagedUploadDeps,
): Promise<StagedUploadResult> {
	const uploaded: StagedAttachment[] = [];
	const failed: StagedAttachment[] = [];
	const resolve = deps.resolveFile ?? resolveStagedFileForUpload;

	for (const staged of files) {
		try {
			const formData = new FormData();
			const fileForUpload = await resolve(staged);
			if (fileForUpload instanceof Blob) {
				formData.append('file', fileForUpload, staged.name);
			} else {
				formData.append('file', fileForUpload as unknown as Blob);
			}
			formData.append('fileName', staged.name);
			formData.append('attachmentType', AttachmentType.BeforePhoto);
			formData.append('workOrderId', workOrderId);

			const upload = await deps.uploadFileToServer(formData);
			if (!upload?.success) {
				failed.push(staged);
				continue;
			}

			if (!deps.isDemoMode) {
				// Demo mode: uploadFileToServer already created the attachment
				// record. Calling createAttachment again duplicates the row.
				await deps.createAttachment({
					workOrderId,
					fileName: upload.fileName,
					fileType: upload.fileType,
					mimeType: upload.mimeType,
					fileSize: upload.fileSize,
					storageKey: upload.storageKey,
					attachmentType: AttachmentType.BeforePhoto,
				});
			}
			uploaded.push(staged);
		} catch (err) {
			console.error('Staged attachment upload failed:', err);
			failed.push(staged);
		}
	}

	return { uploaded, failed };
}

export interface StagedFilesDecision {
	/**
	 * The new value for the staging list. Callers should `setStagedFiles`
	 * to this — it prunes away the files that were just attempted, so no
	 * failed upload can silently carry over into a subsequent submit.
	 */
	nextStaged: StagedAttachment[];
	/** Toast to surface to the user. `null` = no toast. */
	toast: { message: string; variant: 'info' | 'error' } | null;
}

/**
 * Decides what to do with the staging list + what to tell the user after
 * a submit attempt. Pure function so the loss/leak semantics are unit-
 * testable without rendering CreateWorkOrder.
 *
 * Policy (round-3 review of PR #127):
 *   - ALL files that were passed to uploadStagedAttachments are removed
 *     from the staging list, whether they succeeded or failed. Keeping
 *     failures around leaked into the next Save & add another submission
 *     (the failed file from WO A would silently attach to WO B).
 *   - If any failed, surface an ERROR toast that directs the user to the
 *     work order's detail page so they can re-pick and upload there. We
 *     can't retry from here because the local URIs are picker-scoped and
 *     we're about to unmount the form or reset it.
 *   - Successes get an info toast with the count.
 */
export function resolveStagedFilesAfterSubmit(args: {
	currentStaged: StagedAttachment[];
	filesAtSubmit: StagedAttachment[];
	result: StagedUploadResult;
	workOrderLabel: string;
}): StagedFilesDecision {
	const { currentStaged, filesAtSubmit, result, workOrderLabel } = args;

	// Remove EVERY file that was attempted — success or failure — so nothing
	// silently leaks into the next submission.
	const nextStaged = currentStaged.filter(f => !filesAtSubmit.includes(f));

	if (result.failed.length > 0) {
		const failedCount = result.failed.length;
		return {
			nextStaged,
			toast: {
				variant: 'error',
				message: failedCount === 1
					? `1 photo didn't upload. Open ${workOrderLabel} and attach it from the attachments section.`
					: `${failedCount} photos didn't upload. Open ${workOrderLabel} and attach them from the attachments section.`,
			},
		};
	}

	if (result.uploaded.length > 0) {
		return {
			nextStaged,
			toast: {
				variant: 'info',
				message: result.uploaded.length === 1
					? '1 photo uploaded.'
					: `${result.uploaded.length} photos uploaded.`,
			},
		};
	}

	return { nextStaged, toast: null };
}
